import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Table } from 'react-bootstrap';
import { changesApi } from '../../../services/changesApi';
import type { InternalSubtaskListLayoutResponse, InternalSubtaskPresetField } from '../../../types/changes';
import { getApiErrorMessage } from '../../../utils/apiError';
import ChangeInternalSubtaskColumnConfigurator from './ChangeInternalSubtaskColumnConfigurator';
import {
  INTERNAL_SUBTASK_CORE_COLUMN_OPTIONS,
  type InternalSubtaskTableColumnId,
} from '../changeInternalSubtaskTableColumns';

const ChangeInternalSubtaskPresetCatalogPanel: React.FC = () => {
  const [layout, setLayout] = useState<InternalSubtaskListLayoutResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [showColumnConfigurator, setShowColumnConfigurator] = useState(false);
  const [draftSpecKey, setDraftSpecKey] = useState('');
  const [draftLabel, setDraftLabel] = useState('');
  const [draftType, setDraftType] = useState('datetime');
  const [savingPreset, setSavingPreset] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await changesApi.getInternalSubtaskListLayout();
      setLayout(data);
      setError(null);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível carregar campos padrão e colunas.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const catalog = useMemo(() => {
    const presetOptions =
      layout?.preset_fields?.map((field) => ({
        id: `preset:${field.spec_key}` as InternalSubtaskTableColumnId,
        label: field.label,
        group: 'checklist' as const,
        hint: `Chave: ${field.spec_key}`,
      })) || [];
    return [...INTERNAL_SUBTASK_CORE_COLUMN_OPTIONS, ...presetOptions];
  }, [layout?.preset_fields]);

  const catalogIds = useMemo(() => catalog.map((item) => String(item.id)), [catalog]);

  const selectedColumns = useMemo(() => {
    if (!layout?.columns?.length) {
      return INTERNAL_SUBTASK_CORE_COLUMN_OPTIONS.map((item) => item.id);
    }
    return layout.columns
      .filter((item) => item.is_visible)
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((item) => {
        if (item.column_key.startsWith('core:')) {
          const core = item.column_key.replace('core:', '') as InternalSubtaskTableColumnId;
          return core;
        }
        if (item.column_key.startsWith('preset:')) {
          return item.column_key as InternalSubtaskTableColumnId;
        }
        return 'title' as InternalSubtaskTableColumnId;
      });
  }, [layout?.columns]);

  const handleSaveColumns = async (next: InternalSubtaskTableColumnId[]) => {
    const keys = next.map((id) => {
      if (String(id).startsWith('preset:')) return String(id);
      return `core:${id}`;
    });
    try {
      const data = await changesApi.replaceInternalSubtaskListLayout(keys);
      setLayout(data);
      setMessage('Colunas da lista atualizadas.');
      setShowColumnConfigurator(false);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível salvar as colunas.'));
    }
  };

  const handleCreatePreset = async () => {
    setSavingPreset(true);
    setMessage(null);
    try {
      await changesApi.createInternalSubtaskPreset({
        spec_key: draftSpecKey.trim(),
        label: draftLabel.trim(),
        field_type: draftType,
        respondent_role: 'requester',
      });
      setDraftSpecKey('');
      setDraftLabel('');
      await load();
      setMessage('Campo padrão criado. Ele entra em todos os modelos globais e nas novas instâncias.');
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível criar o campo padrão.'));
    } finally {
      setSavingPreset(false);
    }
  };

  const presets = layout?.preset_fields || [];

  return (
    <div>
      {error ? (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      {message ? (
        <Alert variant="success" dismissible onClose={() => setMessage(null)}>
          {message}
        </Alert>
      ) : null}
      <p className="text-muted small">
        Campos com <strong>chave estável</strong> (ex.: <code>planned_start</code>) evitam variações de
        digitação no título. Quem cria a subtarefa interna herda estes campos; campos extras ficam para
        quem assumir a subtarefa responder.
      </p>
      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <strong>Colunas da lista na mudança</strong>
          <Button size="sm" variant="outline-primary" onClick={() => setShowColumnConfigurator(true)}>
            Personalizar colunas
          </Button>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <p className="small text-muted mb-0">Carregando…</p>
          ) : (
            <ul className="small mb-0">
              {(layout?.columns || [])
                .filter((item) => item.is_visible)
                .sort((a, b) => a.sort_order - b.sort_order)
                .map((item) => (
                  <li key={item.column_key}>{item.label}</li>
                ))}
            </ul>
          )}
        </Card.Body>
      </Card>
      <Card className="mb-3">
        <Card.Header><strong>Campos padrão do checklist</strong></Card.Header>
        <Card.Body>
          <Table responsive size="sm" className="mb-3">
            <thead>
              <tr>
                <th>Chave</th>
                <th>Rótulo</th>
                <th>Tipo</th>
                <th>Quem responde</th>
              </tr>
            </thead>
            <tbody>
              {presets.map((field: InternalSubtaskPresetField) => (
                <tr key={field.spec_key}>
                  <td><code>{field.spec_key}</code></td>
                  <td>{field.label}</td>
                  <td>{field.field_type}</td>
                  <td>
                    <Badge bg="secondary">
                      {field.respondent_role === 'assignee' ? 'Grupo executor' : 'Solicitante / dono'}
                    </Badge>
                  </td>
                </tr>
              ))}
              {!presets.length && !loading ? (
                <tr>
                  <td colSpan={4} className="text-muted">Nenhum campo padrão cadastrado.</td>
                </tr>
              ) : null}
            </tbody>
          </Table>
          <div className="border rounded p-3 bg-light">
            <div className="fw-semibold small mb-2">Novo campo padrão (gestor)</div>
            <div className="row g-2 align-items-end">
              <div className="col-md-3">
                <Form.Label className="small">Chave</Form.Label>
                <Form.Control
                  size="sm"
                  value={draftSpecKey}
                  onChange={(e) => setDraftSpecKey(e.target.value)}
                  placeholder="planned_start"
                />
              </div>
              <div className="col-md-4">
                <Form.Label className="small">Rótulo exibido</Form.Label>
                <Form.Control
                  size="sm"
                  value={draftLabel}
                  onChange={(e) => setDraftLabel(e.target.value)}
                  placeholder="Data de início planejada"
                />
              </div>
              <div className="col-md-3">
                <Form.Label className="small">Tipo</Form.Label>
                <Form.Select size="sm" value={draftType} onChange={(e) => setDraftType(e.target.value)}>
                  <option value="text">Texto</option>
                  <option value="date">Data</option>
                  <option value="datetime">Data e hora</option>
                  <option value="list">Lista</option>
                </Form.Select>
              </div>
              <div className="col-md-2">
                <Button
                  size="sm"
                  variant="primary"
                  disabled={savingPreset || !draftSpecKey.trim() || !draftLabel.trim()}
                  onClick={() => void handleCreatePreset()}
                >
                  {savingPreset ? 'Salvando…' : 'Criar'}
                </Button>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
      <ChangeInternalSubtaskColumnConfigurator
        show={showColumnConfigurator}
        onHide={() => setShowColumnConfigurator(false)}
        catalog={catalog}
        selected={selectedColumns}
        catalogIds={catalogIds}
        onChange={(next) => void handleSaveColumns(next)}
      />
    </div>
  );
};

export default ChangeInternalSubtaskPresetCatalogPanel;
